/**
 * When a new paper's results reach the student — straight away, or when the
 * teacher releases them — per kind of attempt. It is the value the create form
 * starts at; the paper and the assignment decide for each sitting.
 *
 * The editor of the `resultRelease` tenant setting, framed by TenantSettingsTab.
 * The setting is overridden whole: saving sets both modalities for this tenant.
 * See docs/FeatureDesigns/assignments-tests/result-release-target.md §2.
 */
import { useState } from 'react';
import { Button, FormField, Select } from '@shared-ui/components';
import type { ResultRelease, TenantResultRelease } from '../../../api/tenantsApi';
import type { SettingEditorProps } from './TenantSettingsTab';

type Modality = 'practice' | 'test';

const MODALITIES: Array<{ key: Modality; label: string }> = [
    { key: 'practice', label: 'Assignments' },
    { key: 'test', label: 'Tests' },
];

const RELEASES: Array<{ value: ResultRelease; label: string }> = [
    { value: 'automatic', label: 'Straight away' },
    { value: 'teacher', label: 'When the teacher releases them' },
];

export function ResultReleaseEditor({ value, save, saving, actions }: SettingEditorProps<'resultRelease'>) {
    const [draft, setDraft] = useState<TenantResultRelease>({ byModality: { ...value.byModality } });
    const releaseOf = (v: TenantResultRelease, key: Modality) => v.byModality[key] ?? 'automatic';
    const dirty = MODALITIES.some(({ key }) => releaseOf(value, key) !== releaseOf(draft, key));

    return (
        <div className="tenants-form">
            {MODALITIES.map(({ key, label }) => (
                <FormField key={key} label={label}>
                    <Select
                        aria-label={`Results — ${label}`}
                        value={releaseOf(draft, key)}
                        onChange={e => setDraft(d => ({ byModality: { ...d.byModality, [key]: e.target.value as ResultRelease } }))}
                    >
                        {RELEASES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </Select>
                </FormField>
            ))}
            <div className="tenant-detail-save">
                {dirty && <span className="tenant-detail-note">Unsaved changes</span>}
                {actions}
                <Button onClick={() => save(draft)} loading={saving} disabled={!dirty}>Save</Button>
            </div>
        </div>
    );
}
